import type {
  KYCDocument,
  PendingDocument,
  DocumentFilters,
  AdminDocumentsResponse,
  KYCStatistics,
  DocumentReviewResponse,
  DocumentUrlResponse,
  DeleteResponse,
  KYCQuestion,
  CreateKYCQuestionInput,
  UpdateKYCQuestionInput,
  ClientKycDetails,
} from './types';
import { apiClient } from '../../../shared/apiClient';
import { getApiBaseUrl } from '@/lib/env';

// Helper function to get adminUserId from cookie
const getAdminUserIdFromCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const storedUser = document.cookie
    .split('; ')
    .find(row => row.startsWith('auth_user='))
    ?.split('=')[1];
  if (!storedUser) return null;
  try {
    const parsedUser = JSON.parse(decodeURIComponent(storedUser));
    return parsedUser.id;
  } catch {
    return null;
  }
};


export const adminKycApi = {
  // Get pending documents (admin)
  getPendingDocuments: async (
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<PendingDocument[]> => {
    const response = await apiClient.get<{ data: PendingDocument[] }>(
      `/admin/kyc/pending?tenantSlug=${encodeURIComponent(tenantSlug)}&limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  // Get all documents with filters (admin)
  getAllDocuments: async (
    tenantSlug: string,
    filters: DocumentFilters = {},
    limit: number = 50,
    offset: number = 0
  ): Promise<AdminDocumentsResponse> => {
    const params = new URLSearchParams({
      tenantSlug,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters.documentType && filters.documentType !== 'all') {
      params.append('documentType', filters.documentType);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<{ data: AdminDocumentsResponse }>(
      `/admin/kyc/documents?${params.toString()}`
    );
    return response.data;
  },

  // Get KYC statistics (admin)
  getKYCStats: async (tenantSlug: string): Promise<KYCStatistics> => {
    const response = await apiClient.get<{ data: KYCStatistics }>(
      `/admin/kyc/stats?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response.data;
  },

  // Approve a document (admin)
  approveDocument: async (
    documentId: string,
    tenantSlug: string,
    adminUserId?: string
  ): Promise<DocumentReviewResponse> => {
    const finalAdminUserId = adminUserId || getAdminUserIdFromCookie();

    const response = await apiClient.post<{ data: DocumentReviewResponse }>(
      `/admin/kyc/${documentId}/approve?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      { adminUserId: finalAdminUserId }
    );
    return response.data;
  },

  // Reject a document (admin)
  rejectDocument: async (
    documentId: string,
    tenantSlug: string,
    rejectionReason: string,
    adminUserId?: string
  ): Promise<DocumentReviewResponse> => {
    const response = await apiClient.post<{ data: DocumentReviewResponse }>(
      `/admin/kyc/${documentId}/reject?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      { adminUserId, rejectionReason }
    );
    return response.data;
  },

  // Delete a document
  deleteDocument: async (
    documentId: string,
    tenantSlug: string,
    clientId: string
  ): Promise<DeleteResponse> => {
    const response = await apiClient.delete<{ data: DeleteResponse }>(
      `/admin/kyc/${documentId}?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response.data;
  },

  // Get signed URL for document
  getDocumentUrl: async (
    documentId: string,
    tenantSlug: string
  ): Promise<DocumentUrlResponse> => {
    const response = await apiClient.get<{ data: DocumentUrlResponse }>(
      `/admin/kyc/${documentId}/url?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response.data;
  },

  // Get document image (authenticated proxy)
  getDocumentImage: async (
    documentId: string,
    tenantSlug: string
  ): Promise<Blob> => {
    const token = apiClient.getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/admin/kyc/${documentId}/image?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      }
    );
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to get document image');
    }
    return res.blob();
  },

  // Get KYC counts for admin stats card
  getKYCCounts: async (tenantSlug: string) => {
    const response = await apiClient.get<{ data: any }>(
      `/admin/kyc/counts?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response.data;
  },

  // Get proxy stream URL for document (when signed URL is not available)
  getProxyStreamUrl: (documentId: string, tenantSlug: string): string => {
    return `${getApiBaseUrl()}/admin/kyc/${documentId}/stream?tenantSlug=${encodeURIComponent(tenantSlug)}`;
  },

  // Get dynamic KYC questions (admin)
  getQuestions: async (tenantSlug: string): Promise<KYCQuestion[]> => {
    const response = await apiClient.get<{ data: KYCQuestion[] }>(
      `/admin/kyc/questions?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response.data;
  },

  // Create dynamic KYC question (admin)
  createQuestion: async (input: CreateKYCQuestionInput): Promise<KYCQuestion> => {
    const response = await apiClient.post<{ data: KYCQuestion }>(
      '/admin/kyc/questions',
      input
    );
    return response.data;
  },

  // Update dynamic KYC question (admin)
  updateQuestion: async (id: string, input: UpdateKYCQuestionInput): Promise<KYCQuestion> => {
    const response = await apiClient.put<{ data: KYCQuestion }>(
      `/admin/kyc/questions/${id}`,
      input
    );
    return response.data;
  },

  // Delete dynamic KYC question (admin)
  deleteQuestion: async (id: string, tenantSlug: string): Promise<any> => {
    const response = await apiClient.delete<any>(
      `/admin/kyc/questions/${id}?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response;
  },

  // Get client details for KYC view (name, dateOfBirth, city, state, country only)
  getClientDetails: async (
    clientId: string,
    tenantSlug: string
  ): Promise<ClientKycDetails> => {
    const response = await apiClient.get<{ data: ClientKycDetails }>(
      `/admin/kyc/client/${clientId}/details?tenantSlug=${encodeURIComponent(tenantSlug)}`
    );
    return response.data;
  },
};

// Standalone export functions for backward compatibility
export async function getDocumentUrl(
  documentId: string,
  tenantSlug: string
): Promise<DocumentUrlResponse> {
  return adminKycApi.getDocumentUrl(documentId, tenantSlug);
}

export function getProxyStreamUrl(documentId: string, tenantSlug: string): string {
  return adminKycApi.getProxyStreamUrl(documentId, tenantSlug);
}

export async function getClientDetails(
  clientId: string,
  tenantSlug: string
): Promise<ClientKycDetails> {
  return adminKycApi.getClientDetails(clientId, tenantSlug);
}

export default adminKycApi;
